import { convertDateFormat, formatNoteTimestamp, titleCaseString } from "@/utils";
import { buildStatusNotesPayload } from "@/utils/statusNotes";
import { replaceTableMarkers } from "@/utils/pastedTable";

/** Status-update notes as an indented multi-line block: `  Author (timestamp): text` per note. */
function formatStatusNotes(statusNotes) {
  const payload = buildStatusNotesPayload(statusNotes);
  if (!payload) return "";
  const noteLines = payload.notes.map((note) => {
    const timestamp = formatNoteTimestamp(note.createdOn);
    const meta = [note.author || "System", timestamp ? `(${timestamp})` : ""].filter(Boolean).join(" ");
    return `  ${meta}: ${replaceTableMarkers(note.note)}`;
  });
  return "\n" + noteLines.join("\n");
}

/**
 * Format a submission's details as "Label: Value" lines for pasting into
 * external systems (LAM-4465). Operates on the nested order shape returned by
 * both the order-detail and order-list APIs — grid rows must pass `row.raw`.
 * Empty fields are kept with blank values.
 */
export function formatSubmissionDetails(order) {
  const patientName = [order.patientFirstName, order.patientLastName].filter(Boolean).join(" ");
  const lines = [
    ["Patient Name", patientName],
    ["Patient DOB", order.patientDob ? convertDateFormat(order.patientDob) : ""],
    ["Status", order.status || ""],
    ["Date Created", order.createdOn ? convertDateFormat(order.createdOn) : ""],
    ["Date Modified", order.modifiedOn ? convertDateFormat(order.modifiedOn) : ""],
    ["Lamar Health Key", order.orderKey || ""],
    ["External Portal Key", order.orderSubmission?.portalSubmissionId || ""],
    ["Medication", order.medication ? titleCaseString(order.medication) : ""],
    ["Insurance/Form Name", order.insuranceProvider ? titleCaseString(order.insuranceProvider) : ""],
    ["Plan Type", order.insurancePlanType || ""],
    ["Prior Auth Number", order.orderSubmission?.priorAuthNumber || ""],
    ["Rx Number", order.prescriptionNumber || ""],
    ["MRN", order.patientExternalId || ""],
    ["Status Notes", formatStatusNotes(order.statusNotes)],
  ];
  return lines.map(([label, value]) => `${label}: ${value}`).join("\n");
}

/** Copy text to the clipboard; falls back to execCommand when the async API is unavailable or denied. */
export async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
